import { CHART } from '../constants';
import { scaleLinear, scaleTime } from '@visx/scale';
import { extent } from 'd3-array';
import { useMemo } from 'react';

interface NormalisedDataPoint {
  originalValue: number | null;
  value: number;
}

interface NormalisedValuation {
  originalFairValue: number | null;
  fairValue: number | null;
  createdAt: string;
}

interface UseChartScalesProps {
  width: number;
  height: number;
  keys: number[];
  normalisedEntities: Record<number, NormalisedDataPoint>;
  normalisedValuationsByNarrative?: (NormalisedValuation[] | undefined)[];
  periodStart: number;
  periodEnd: number;
}

// TODO: Add some memo'ing in here
export const useChartScales = ({
  width,
  height,
  keys,
  normalisedEntities,
  normalisedValuationsByNarrative,
  periodStart,
  periodEnd,
}: UseChartScalesProps) => {
  // Set up the time scale for X axis (horizontal)
  const xScale = useMemo(
    () =>
      scaleTime<number>({
        range: [CHART.PADDING, width - CHART.PADDING],
        domain:
          keys.length > 0
            ? [keys[0], keys[keys.length - 1]]
            : [periodStart, periodEnd], // TODO: Remove this?
        clamp: true,
      }),
    [width, keys, periodStart, periodEnd]
  );

  // Find the minimum and maximum percentages to set chart boundaries
  // Include all narratives' fair values in the calculation
  const allNarrativeValues = useMemo(
    () =>
      normalisedValuationsByNarrative?.flatMap(
        (narrative) =>
          narrative
            ?.map((v) => v.fairValue)
            .filter((v): v is number => v !== null && !isNaN(v)) || []
      ) || [],
    [normalisedValuationsByNarrative]
  );

  const values = useMemo(
    () => [
      ...Object.values(normalisedEntities).map((e) => e.value),
      ...allNarrativeValues,
    ],
    [normalisedEntities, allNarrativeValues]
  );

  const [minY, maxY] = useMemo(
    () =>
      values.length > 0
        ? extent(values.filter((v): v is number => typeof v === 'number')) ?? [
            0, 0,
          ]
        : [0, 0],
    [values]
  );

  // Set up the percentage scale for Y axis (vertical)
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [CHART.PADDING, height - CHART.PADDING],
        domain: [
          Math.max(maxY || 0, 0), // Highest percentage
          Math.min(minY || 0, 0), // Lowest percentage
        ],
        clamp: true,
      }),
    [height, maxY, minY]
  );

  return { xScale, yScale };
};
